<template>
  <v-row style="position: relative">
    <v-col v-blur="blurConfig" cols="10">
      <h3 class="d-flex justify-start mb-3">{{ $t('chart.title') }}</h3>
      <p class="d-flex justify-start">
        {{ $t('chart.subtitle') }}
        <v-select :items="types" v-model="typeSelected" class="ml-5 mt-n5" style="max-width: 20%" @change="typeChanged"
          dense outlined />
      </p>
      <apexchart :options="optionsMonthlySwisetRatio" :series="seriesMonthlySwisetRatio" class="d-flex justify-center"
        type="line" height="400" />
    </v-col>
    <v-col v-blur="blurConfig" class="d-flex align-center justify-center" cols="2">
      <div>
        <h1 :style="(swisetRatio > 1) ? 'color: #0F7500' : 'color: #FF4747'">{{ swisetRatio.toFixed(2) }}</h1>
        <h3 class='mb-3'>{{ $t('global.title') }}</h3>
        <p class="mt-5" v-html="$t('global.explanation')" />
      </div>
    </v-col>
    <v-overlay :dark="false" v-model="canSeeStat" :absolute="true" opacity="0">
      <ProStatAlert @goToPro="goToPro" />
    </v-overlay>
  </v-row>
</template>

<script>
import Vue from 'vue';
import vBlur from 'v-blur';
import ProStatAlert from "@/views/Journal/components/ProStatAlert";

Vue.use(vBlur);

export default {
  name: "SwisetRatioStats",
  components: { ProStatAlert },
  data() {
    return {
      blurConfig: {
        isBlurred: !this.$store.state.user.isPro,
        opacity: 0.07,
        filter: "blur(1.2px)",
        transition: "all .3s linear"
      },
      canSeeStat: !this.$store.state.user.isPro,
      types: ['By Account', 'By Strategy'],
      typeSelected: 'By Account',
      swisetRatio: 0,
      accountsBasicData: [],
      strategiesBasicData: [],
      seriesMonthlySwisetRatio: [
        {
          name: "Break Even",
          type: 'line',
          data: [{ x: 12.50, y: 7 }, { x: 13.33, y: 6.5 }, { x: 14.29, y: 6 }, { x: 15.38, y: 5.5 }, { x: 16.67, y: 5 }, { x: 18.18, y: 4.5 },
          { x: 20, y: 4 }, { x: 22.22, y: 3.5 }, { x: 25, y: 3 }, { x: 28.57, y: 2.5 }, { x: 33.33, y: 2 }, { x: 40, y: 1.5 }, { x: 50, y: 1 }, { x: 66.67, y: 0.5 }, { x: 100, y: 0 }],
        },
      ],
      optionsMonthlySwisetRatio: {
        theme: {
          mode: this.$store.state.currentTheme == "dark" ? "dark" : "light"
        },
        chart: {
          height: 350,
          type: 'line',
        },
        colors: ['rgb(0,103,191)', 'rgb(0,165,83)'],
        dataLabels: {
          enabled: true,
          formatter: function (value) {
            return `${(value.toFixed(2) * 1).toString()}`;
          },
        },
        fill: {
          type: 'solid',
        },
        markers: {
          size: [0, 10]
        },
        stroke: {
          lineCap: 'curve',
          width: 3
        },
        tooltip: {
          x: {
            show: false,
          },
          y: {
            formatter: function (value, opts) {
              if (opts !== undefined) {
                let isBreakEven = opts.seriesIndex === 0;
                if (isBreakEven) {
                  let winRatio = (1 / (value + 1)) * 100;
                  return `${value} Risk/Reward<br>${winRatio.toFixed(1)}% Win Rate`;
                }
                let winRate = opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex].x;
                let swisetRatio = ((winRate * value) + winRate) / 100;
                let accountInfo = opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex].description;
                return `${accountInfo}<br><br>${swisetRatio.toFixed(2)} Swiset Ratio<br>${value.toFixed(2)} Risk/Reward<br>${winRate.toFixed(1)}% Win Rate`;
              }
              else
                return `${value} Risk/Reward`;
            }
          },
        },
        yaxis: {
          title: { text: 'Risk / Reward' },
          labels: {
            formatter: function (value) {
              return `${value.toFixed(1)}`;
            },
          }
        },
        xaxis: {
          type: 'numeric',
          min: 0,
          max: 100,
          title: { text: 'Win Rate Percentage (%)' },
          labels: {
            formatter: function (value) {
              return `${value.toFixed(1)}%`;
            },
          }
        },
      },
    };
  },
  watch: {
    '$store.state.book.trades': function () {
      this.refreshStats();
    }
  },
  mounted() {
    this.refreshStats();
  },
  methods: {
    typeChanged() {
      this.refreshStats();
    },
    resetStats() {
      this.seriesMonthlySwisetRatio = [
        {
          name: "Break Even",
          type: 'line',
          data: [{ x: 12.50, y: 7 }, { x: 13.33, y: 6.5 }, { x: 14.29, y: 6 }, { x: 15.38, y: 5.5 }, { x: 16.67, y: 5 }, { x: 18.18, y: 4.5 },
          { x: 20, y: 4 }, { x: 22.22, y: 3.5 }, { x: 25, y: 3 }, { x: 28.57, y: 2.5 }, { x: 33.33, y: 2 }, { x: 40, y: 1.5 }, { x: 50, y: 1 }, { x: 66.67, y: 0.5 }, { x: 100, y: 0 }],
        },
      ];
    },
    refreshStats() {
      this.resetStats();
      let copyOfTrades = (this.typeSelected === 'By Account') ? this.$store.state.book.trades.slice() : this.$store.state.book.trades.filter((trade) => trade.strategy !== null);
      let map = {};
      this.accountsBasicData = [];
      this.strategiesBasicData = [];
      let globalPositiveTrades = 0, globalNegativeTrades = 0, globalPositiveSum = 0, globalNegativeSum = 0;
      let totalTrades = copyOfTrades.length;

      if (totalTrades > 0) {
        copyOfTrades.forEach((trade) => {
          let mapIdentifier = (this.typeSelected === 'By Account') ? trade.brokerAccount.id : trade.strategy.id;
          let localPositiveTrades = 0, localNegativeTrades = 0, localPositiveSum = 0, localNegativeSum = 0, localTrades = 0;

          if (!(mapIdentifier in map)) {
            map[mapIdentifier] = [0, 0, 0, 0, 0];
            // By Account
            if (this.typeSelected === 'By Account') this.accountsBasicData.push({ id: mapIdentifier, identifier: trade.brokerAccount.identifier, brokerName: trade.brokerAccount.broker.name });
            // By Watchlist
            else this.strategiesBasicData.push({ id: mapIdentifier, name: trade.strategy.name });
          }
          else {
            localPositiveTrades = map[mapIdentifier][0];
            localNegativeTrades = map[mapIdentifier][1];
            localPositiveSum = map[mapIdentifier][2];
            localNegativeSum = map[mapIdentifier][3];
            localTrades = map[mapIdentifier][4];
          }

          let netProfit = trade.netProfit;
          if (netProfit > 0) {
            localPositiveSum += netProfit;
            globalPositiveSum += netProfit;
            globalPositiveTrades++;
            localPositiveTrades++;
          } else {
            globalNegativeSum += netProfit;
            localNegativeSum += netProfit;
            globalNegativeTrades++;
            localNegativeTrades++;
          }
          localTrades++;

          map[mapIdentifier] = [localPositiveTrades, localNegativeTrades, localPositiveSum, localNegativeSum, localTrades];
        });

        // Global Swiset Ratio Calculation
        let globalRiskRewardRatio = (globalPositiveTrades === 0 || globalNegativeTrades === 0 || globalNegativeSum === 0) ? 4 :
          Math.abs((globalPositiveSum / globalPositiveTrades) / (globalNegativeSum / globalNegativeTrades));
        let globalWinRatio = globalPositiveTrades / totalTrades;
        this.swisetRatio = (globalWinRatio * globalRiskRewardRatio) + globalWinRatio;

        let relationData = [];
        for (let mapIdentifier in map) {
          let elementFound = (this.typeSelected === 'By Account') ? this.accountsBasicData.find((account) => account.id === (mapIdentifier * 1))
            : this.strategiesBasicData.find((strategy) => strategy.id === (mapIdentifier * 1));
          let elementPositiveTrades = map[mapIdentifier][0];
          let elementNegativeTrades = map[mapIdentifier][1];
          let elementPositiveSum = map[mapIdentifier][2];
          let elementNegativeSum = map[mapIdentifier][3];
          let elementTrades = map[mapIdentifier][4];
          let elementRiskRewardRatio = (elementPositiveTrades === 0 || elementNegativeTrades === 0 || elementNegativeSum === 0) ? 4 :
            Math.abs((elementPositiveSum / elementPositiveTrades) / (elementNegativeSum / elementNegativeTrades));
          let elementWinRatio = elementPositiveTrades / elementTrades;
          relationData.push({
            x: (elementWinRatio * 100),
            y: elementRiskRewardRatio,
            description: (this.typeSelected === 'By Account') ? `${elementFound.brokerName}${(elementFound.identifier !== undefined) ? ` - ${elementFound.identifier}` : ''}`
              : elementFound.name,
          });
        }
        this.seriesMonthlySwisetRatio.push({ name: (this.typeSelected === 'By Account') ? 'Accounts' : 'Strategies', type: 'scatter', data: relationData, },
        );
      }
      else
        this.swisetRatio = 0;
    },
    goToPro() {
      this.$emit('goToPro');
    }
  },
}
</script>



<style scoped></style>
